/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Context: AuthContext
 * File     : AuthContext.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React Context for AuthContext, supplying state and actions to the component tree.
 *   Exports: AuthProvider, useAuth
 *   
 *
 * Dependencies:
 *   - ../services/auth
 *   - react
 *
 * Usage:
 *   Wrap app: <AuthContextProvider>...children...</AuthContextProvider>
 * -----------------------------------------------------------------------------
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { loadToken, clearToken, login as authLogin, register as authRegister, me as authMe, saveToken } from '../services/auth';

const AuthContext = createContext({ user: null, login: async () => {}, register: async () => {}, logout: () => {} });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // First, capture token from URL hash (Discord callback)
    try {
      const hash = window.location.hash || '';
      const m = hash.match(/token=([^&]+)/);
      if (m && m[1]) {
        saveToken(decodeURIComponent(m[1]));
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
    } catch (_) {}

    const token = loadToken();
    if (!token) {
      setLoading(false);
      return;
    }
    authMe()
      .then((data) => setUser(data.user))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  // Re-evaluate on route changes to catch token placed in hash after first mount
  useEffect(() => {
    const hash = window.location.hash || '';
    const m = hash.match(/token=([^&]+)/);
    if (m && m[1]) {
      setLoading(true);
      saveToken(decodeURIComponent(m[1]));
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      authMe()
        .then((data) => setUser(data.user))
        .catch(() => clearToken())
        .finally(() => setLoading(false));
    }
  }, [location.pathname, location.search, location.hash]);

  const login = async (credentials) => {
    const data = await authLogin(credentials);
    setUser(data.user);
    return data;
  };

  const register = async (credentials) => {
    const data = await authRegister(credentials);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const data = await authMe();
      setUser(data.user);
      return data.user;
    } catch (e) {
      clearToken();
      setUser(null);
      throw e;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);


